/**
 * Minimal, memory-only GET cache + in-flight registry around `apiFetch`
 * (design.md section 7). No new dependency introduced (design.md section 13
 * "Query library" decision) — task 2.5 requires stopping for approval before
 * reaching for TanStack Query if this ever needs retries/infinite/offline.
 * Query keys are built by callers from resource + normalized params/IDs
 * only; this module never inspects key contents so it cannot leak
 * tokens/PII/`client_id` itself.
 */

export type QueryKey = readonly unknown[];

/** Documented stale-time defaults by resource (design.md section 7); no feature consumes these yet in this foundation slice. */
export const DEFAULT_STALE_TIME_MS = {
  overview: 30_000,
  campaignMonitoringActive: 10_000,
  operationalHealth: 60_000,
  listsDetailsSelectors: 60_000,
} as const;

interface CacheEntry<T> {
  data: T;
  updatedAt: number;
}

interface InflightEntry<T> {
  promise: Promise<T>;
  controller: AbortController;
  /** The key's current fetch generation at the time this entry was created — see `isCurrentGeneration`. */
  generation: number;
}

const dataCache = new Map<string, CacheEntry<unknown>>();
const inflight = new Map<string, InflightEntry<unknown>>();
// Codex finding (round 4, PR #381): a listener used to always mean "go
// check the cache / refetch" — correct for an actual invalidation (nothing
// cached yet) and for a successful broadcast (fresh data IS cached), but
// wrong for a FAILURE broadcast: nothing new is ever cached on failure, so
// every listener (including the failing fetch's own, since it is a
// subscriber of its own key too) fell through to an unwanted extra
// `runFetch(false)` call instead of just settling out of its loading phase.
// The reason lets `useQuery`'s listener tell these apart without guessing
// from cache state alone.
export type NotifyReason = "data" | "failure";
const subscribers = new Map<string, Set<(reason: NotifyReason, error?: unknown) => void>>();
/** Per-key "current fetch" counter shared across every caller (review fix P1: cross-instance latest-wins — this must NOT live in a single hook instance's local ref). */
const generationByKey = new Map<string, number>();

/** Query keys must never carry `client_id` (design.md section 7). This is the one segment shape mechanically enforceable here; token/PII shape discipline stays with each call site per this module's own key-agnostic design. */
const FORBIDDEN_KEY_SEGMENT = /^client_?id$/i;

function assertSafeQueryKey(key: QueryKey): void {
  for (const segment of key) {
    if (typeof segment === "string" && FORBIDDEN_KEY_SEGMENT.test(segment)) {
      throw new Error(`Query key must not contain a client_id segment: ${JSON.stringify(key)}`);
    }
  }
}

/** `true` if `generation` is still this key's current (i.e. not superseded by a later force-replace) — checked from `useQuery`'s resolve/reject handlers instead of a per-hook-instance ref. */
export function isCurrentGeneration(key: QueryKey, generation: number): boolean {
  return (generationByKey.get(serializeQueryKey(key)) ?? 0) === generation;
}

/** Deterministic string key for a `QueryKey` array — same shape in, same string out. */
export function serializeQueryKey(key: QueryKey): string {
  return JSON.stringify(key);
}

export function getCachedEntry<T>(key: QueryKey): CacheEntry<T> | undefined {
  return dataCache.get(serializeQueryKey(key)) as CacheEntry<T> | undefined;
}

export function setCachedEntry<T>(key: QueryKey, data: T): number {
  const updatedAt = Date.now();
  dataCache.set(serializeQueryKey(key), { data, updatedAt });
  return updatedAt;
}

function notify(keyStr: string, reason: NotifyReason = "data", error?: unknown): void {
  subscribers.get(keyStr)?.forEach((listener) => listener(reason, error));
}

/**
 * GET dedup + abort/latest-wins: `force:false` reuses an in-flight promise
 * for the same key (fetcher not re-invoked); `force:true` aborts any
 * in-flight request for the key and starts a fresh one.
 */
export function startFetch<T>(
  key: QueryKey,
  fetcher: (signal: AbortSignal) => Promise<T>,
  options: { force: boolean },
): InflightEntry<T> {
  assertSafeQueryKey(key);
  const keyStr = serializeQueryKey(key);
  const existing = inflight.get(keyStr) as InflightEntry<T> | undefined;

  if (existing && !options.force) {
    return existing;
  }

  if (existing && options.force) {
    existing.controller.abort();
  }

  // A genuinely new fetch (first for this key, or a forced replace) bumps
  // the shared generation so every caller watching this key — not just the
  // one that called startFetch — can tell a superseded response apart from
  // the current one via `isCurrentGeneration`.
  const generation = (generationByKey.get(keyStr) ?? 0) + 1;
  generationByKey.set(keyStr, generation);

  const controller = new AbortController();
  const promise = fetcher(controller.signal).finally(() => {
    // Only clear if this is still the current entry (a later force replaced it otherwise).
    if (inflight.get(keyStr)?.promise === promise) {
      inflight.delete(keyStr);
    }
  });
  const entry: InflightEntry<T> = { promise, controller, generation };
  inflight.set(keyStr, entry);
  return entry;
}

/**
 * Broadcasts a successful fetch's fresh cache entry to every OTHER mounted
 * `useQuery` instance subscribed to this exact key — not just the instance
 * whose own fetch happened to resolve.
 *
 * Codex finding (panel-redesign-v3 PR #381): a force-refetch from one
 * instance (e.g. the shell's background poll or its focus-triggered
 * refresh) aborts another mounted instance's own in-flight request for the
 * SAME key (`startFetch`'s force path). The aborted instance's promise
 * rejects with `AbortError` and its `isCurrentGeneration` check short-
 * circuits before touching state, so it is left stuck in whatever phase it
 * was in when the abort happened (typically `"loading"`,
 * i.e. "Actualizando..." shown indefinitely) — nothing ever tells it that a
 * newer response for its own key has already arrived. Calling this right
 * after the winning fetch's `setCachedEntry` lets `useQuery`'s own
 * `subscribeInvalidate` listener (already wired for mutation invalidation)
 * adopt the fresh cached data directly, with no extra network request.
 */
export function notifySubscribers(key: QueryKey): void {
  notify(serializeQueryKey(key), "data");
}

/**
 * Codex finding (round 4, PR #381): `notifySubscribers` above only ever ran
 * after a SUCCESSFUL fetch — when the winning (current-generation) request
 * for a key fails instead, a different subscriber whose own in-flight
 * request this one aborted was never told the race settled at all, leaving
 * it stuck in its loading phase ("Actualizando...") indefinitely. Unlike a
 * success, nothing new is cached on a failure, so listeners must be told
 * `"failure"` explicitly (see `NotifyReason`) rather than being left to
 * infer it from an unchanged cache — inferring it that way is exactly what
 * previously caused an unwanted extra `runFetch(false)` call on every
 * instance (including the failing fetch's own self-subscription).
 *
 * Codex finding (round 6, PR #381): a subscriber with no cached data (e.g.
 * one whose own in-flight request this failure's winning fetch aborted)
 * only had its `phase` flipped to `"error"` by the plain reason string —
 * `error` itself stayed `undefined`, so screens that gate their retry panel
 * on `error !== undefined` (OverviewScreen) rendered neither the stale data
 * nor a retry affordance. Forwarding the actual rejection value lets every
 * settled subscriber show a real error, not just an empty page.
 */
export function notifyFailure(key: QueryKey, error: unknown): void {
  notify(serializeQueryKey(key), "failure", error);
}

/** Listener invoked when `invalidateExact` matches this key, a sibling instance's fetch for it settles, or an actual mutation invalidates it — lets `useQuery` react (refetch, adopt fresh data, or settle) depending on `reason`. */
export function subscribeInvalidate(
  key: QueryKey,
  listener: (reason: NotifyReason, error?: unknown) => void,
): () => void {
  const keyStr = serializeQueryKey(key);
  const set = subscribers.get(keyStr) ?? new Set();
  set.add(listener);
  subscribers.set(keyStr, set);
  return () => {
    set.delete(listener);
  };
}

/**
 * Mutation success invalidation: removes cached data for exact keys and
 * notifies their subscribers to refetch.
 *
 * Codex finding (P2, revision automatica sobre PR #285): dropping only the
 * CACHED data wasn't enough -- if a fetch for this key was ALREADY in
 * flight from before the mutation (e.g. another consumer's own auto-fetch,
 * or a poll), every notified subscriber's `runFetch(false)` would dedupe
 * onto that PRE-mutation promise instead of starting a fresh one, so its
 * (stale) response would get re-cached and the mutation's effect (e.g. a
 * newly created Show) could stay invisible indefinitely. Bumping the
 * generation here marks that stale in-flight fetch as superseded (its
 * resolution will fail `isCurrentGeneration` and be ignored, same
 * mechanism the cross-instance latest-wins fix already relies on) and
 * dropping its `inflight` entry means the FIRST subscriber notified below
 * sees nothing to dedupe onto and starts a genuinely fresh, post-mutation
 * fetch -- every subsequent subscriber in this same notify loop then
 * dedupes onto THAT one instead (queryClient's own `inflight` registry is
 * set synchronously inside `startFetch`, before `notify`'s loop reaches
 * the next listener).
 */
export function invalidateExact(keys: readonly QueryKey[]): void {
  for (const key of keys) {
    const keyStr = serializeQueryKey(key);
    dataCache.delete(keyStr);
    generationByKey.set(keyStr, (generationByKey.get(keyStr) ?? 0) + 1);
    const staleInflight = inflight.get(keyStr);
    if (staleInflight) {
      staleInflight.controller.abort();
      inflight.delete(keyStr);
    }
    notify(keyStr);
  }
}

/**
 * Same per-key effect as `invalidateExact` (delete cached data, bump
 * generation, abort+drop any in-flight fetch, notify subscribers) applied to
 * EVERY key currently known to this module whose leading segments equal
 * `prefix`, instead of an exact caller-supplied list.
 *
 * PR #349 (bulk-schedule), Codex round 4 finding: `invalidateExact` requires
 * the caller to already know every exact key to evict. Two bulk-schedule
 * gaps proved that assumption wrong -- (1) a batch can span pages, so a
 * sibling's `campaign_id` scheduled server-side may never have been loaded
 * client-side, so its `["campaign", id]` detail key could never be named
 * exactly; (2) the campaign list itself is cached per page/filter
 * combination (`["campaigns", estado, page, size]`), so an operator who
 * previously viewed a different page or a different `estado` filter has a
 * cached list entry this screen's own `queryKey` can never name either.
 * `invalidatePrefix(["campaign"])` / `invalidatePrefix(["campaigns"])` evict
 * every such variation in one call, with no need to enumerate them.
 *
 * A key "matches" `prefix` when it has at least as many segments as `prefix`
 * and each of `prefix`'s segments is `Object.is`-equal to the key's segment
 * at the same position -- checked STRUCTURALLY on the decoded array, never
 * as a raw string-prefix check on `serializeQueryKey`'s JSON output. A raw
 * string check would false-positive: `serializeQueryKey(["campaign"])` is
 * `["campaign"]`, and the string `["campaignSomethingElse",1]` (a
 * hypothetical key whose first segment merely starts with the same
 * characters) is NOT a `.startsWith` match against `["campaign"]` (the `]`
 * right after the closing quote never lines up), but a shorter, more naive
 * separator-based scheme could still be fooled by a key/segment shape this
 * module doesn't currently have -- decoding back into an array and comparing
 * element-by-element removes that whole class of ambiguity rather than
 * relying on today's key shapes never producing it.
 *
 * Iterates the UNION of every key string known to `dataCache`, `subscribers`,
 * and `inflight` -- a key can exist in one of these maps without existing in
 * either of the others (e.g. a key with only a subscriber and no cached data
 * yet, or an in-flight fetch for a key nothing has subscribed to), so
 * checking only one map would silently miss real matches tracked by another.
 */
export function invalidatePrefix(prefix: QueryKey): void {
  const candidateKeyStrs = new Set<string>([...dataCache.keys(), ...subscribers.keys(), ...inflight.keys()]);

  for (const keyStr of candidateKeyStrs) {
    let decoded: unknown;
    try {
      decoded = JSON.parse(keyStr);
    } catch {
      // Every key string in these maps was produced by this module's own
      // `serializeQueryKey` (JSON.stringify), so this should be unreachable;
      // skip defensively rather than throw on a corrupt/foreign entry.
      continue;
    }
    if (!Array.isArray(decoded) || decoded.length < prefix.length) {
      continue;
    }
    const isMatch = prefix.every((segment, index) => Object.is(decoded[index], segment));
    if (!isMatch) {
      continue;
    }

    dataCache.delete(keyStr);
    generationByKey.set(keyStr, (generationByKey.get(keyStr) ?? 0) + 1);
    const staleInflight = inflight.get(keyStr);
    if (staleInflight) {
      staleInflight.controller.abort();
      inflight.delete(keyStr);
    }
    notify(keyStr);
  }
}

/** Account change/logout: drops all cached data and aborts every in-flight request. Route scroll/disclosure metadata lives in `sessionStorage`, unaffected here. */
export function clearAllQueries(): void {
  dataCache.clear();
  for (const entry of inflight.values()) {
    entry.controller.abort();
  }
  inflight.clear();
  // Bump — never reset to a low/reusable number, and never merely "leave
  // alone" — every key's generation (review fix round 3). `isCurrentGeneration`
  // is a strict `===` check, so leaving the counter untouched (round 2's
  // fix) only protected against collision with a FUTURE fetch for that key;
  // it did nothing if no fetch for that key ever happens again (e.g. logout
  // with no revisit to that screen) — a still-pending pre-clear request
  // that ignores its AbortSignal and resolves late would keep matching its
  // own never-invalidated generation forever, repopulating the cache and
  // updating any still-mounted hook's state. Bumping makes every
  // previously-issued generation for that key fail the check immediately,
  // with no dependency on a future fetch. The counter was already strictly
  // increasing, so the bumped value exceeds every prior generation; the
  // next real `startFetch` computes `(bumped value) + 1`, still strictly
  // greater than anything before — no new collision risk introduced.
  for (const [keyStr, generation] of generationByKey) {
    generationByKey.set(keyStr, generation + 1);
  }
}
